import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  FormSizes,
  Button,
  ButtonVariants,
  ButtonSizes,
  FormValidateOn,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { ROUTES } from '@/app/routes';
import { ALLOCATION_STATUS } from '@/config/statuses';
import { LoadingOverlay, EmptyState } from '@/components/feedback';
import {
  useGetAllocationDetailQuery,
  useGetAllocationDeclarationQuery,
} from '../api/allocationsApi';
import { useDeclarationPdf, useSaveDeclaration, useUploadDeclarationFile } from '../hooks';
import { buildEditDeclarationValues } from '../constants/editDeclarationValues';
import { DeclarationDetailsStep } from '../components/EditDeclaration/DeclarationDetailsStep';
import { MaterialsDetailsStep } from '../components/EditDeclaration/MaterialsDetailsStep';
import { EmissionDetailsStep } from '../components/EditDeclaration/EmissionDetailsStep';
import { DeclarationSummaryStep } from '../components/EditDeclaration/DeclarationSummaryStep';
import styles from './EditAllocationDeclarationPage.module.scss';

const STEPS = Object.freeze({ declaration: 0, materials: 1, emissions: 2, summary: 3 });
const LAST_STEP = STEPS.summary;

// Outbounds ▸ Allocations ▸ Edit declaration — a full-page form, like Validate declaration.
export const EditAllocationDeclarationPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, allocationId } = useParams();
  const [step, setStep] = useState(STEPS.declaration);

  const {
    data: allocation,
    isLoading,
    isError,
  } = useGetAllocationDetailQuery({ module, id: allocationId });

  // <Form> seeds initialValues once, on mount, so it waits for the carried-over fields too.
  const { data: declaration, isLoading: isDeclarationLoading } = useGetAllocationDeclarationQuery({
    module,
    id: allocationId,
  });

  const goBack = useCallback(
    () => navigate(ROUTES.allocationDetail(module, allocationId)),
    [navigate, module, allocationId],
  );

  const steps = useMemo(
    () => [
      { label: t('ALLOCATIONS_DECLARATION_STEP_DETAILS') },
      { label: t('ALLOCATIONS_DECLARATION_STEP_MATERIALS') },
      { label: t('ALLOCATIONS_DECLARATION_STEP_EMISSIONS') },
      { label: t('DECLARATIONS_STEP_SUMMARY') },
    ],
    [t],
  );

  const initialValues = useMemo(
    () => buildEditDeclarationValues(allocation, declaration),
    [allocation, declaration],
  );
  // The site's (or B2B account's) own ISCC certificate; only a site without one leaves it to the user.
  const isSupplierCertificateFromServer = Boolean(declaration?.supplierCertificateNumber);

  // Next runs the step's own validation through submit; only the last step actually saves.
  const isAdvancingRef = useRef(false);
  // The last validated values, so Summary can render and upload the PDF before Save is clicked.
  const [summaryValues, setSummaryValues] = useState(null);

  // PUT is rejected unless a declaration exists, so the method follows the status, not the page.
  const isEdit = allocation?.statusCode === ALLOCATION_STATUS.declarationGenerated;
  const { save, isSaving } = useSaveDeclaration({ module, allocationId, isEdit });
  const uploadDeclarationFile = useUploadDeclarationFile(module);

  const { previewBlob } = useDeclarationPdf({
    module,
    allocationId,
    values: summaryValues,
    enabled: step === LAST_STEP && summaryValues != null,
    // The edited values are the point, so this always renders and uploads a new document.
    reuseStoredFile: false,
  });

  const handleValidated = useCallback(
    async (values) => {
      const withSite = { ...values, siteId: allocation?.siteId };

      if (isAdvancingRef.current) {
        isAdvancingRef.current = false;
        setSummaryValues(withSite);
        setStep((previous) => Math.min(previous + 1, LAST_STEP));
        return;
      }

      try {
        // The document is uploaded here, not on render, so an abandoned edit writes nothing.
        const uploadedFileId = await uploadDeclarationFile(allocationId, previewBlob);
        await save(withSite, uploadedFileId).unwrap();
        goBack();
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [save, allocation?.siteId, uploadDeclarationFile, allocationId, previewBlob, goBack],
  );

  const handlePrevious = useCallback(() => setStep((previous) => Math.max(previous - 1, 0)), []);

  if (isLoading || isDeclarationLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !allocation) {
    return (
      <EmptyState
        title={t('ALLOCATIONS_DETAIL_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goBack}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <Form
      validateOn={FormValidateOn.change}
      initialValues={initialValues}
      onSubmit={handleValidated}
      size={FormSizes.large}
      className={styles.page}
    >
      {({ submit }) => {
        const handleNext = () => {
          isAdvancingRef.current = true;
          submit();
        };

        return (
          <>
            <Form.Header
              title={t('ALLOCATIONS_EDIT_DECLARATION_TITLE')}
              onClose={goBack}
              closeLabel={t('COMMON_CLOSE')}
            />

            <div className={styles.stepperBar}>
              <Form.Stepper
                steps={steps}
                activeStep={step}
                variant="flush"
                className={styles.stepper}
              />
            </div>

            <div className={styles.body}>
              {step === STEPS.declaration && (
                <DeclarationDetailsStep
                  supplierCertificateLocked={isSupplierCertificateFromServer}
                />
              )}
              {step === STEPS.materials && <MaterialsDetailsStep />}
              {step === STEPS.emissions && <EmissionDetailsStep />}
              {step === STEPS.summary && <DeclarationSummaryStep />}
            </div>

            <Form.Footer align="between">
              <Button
                type="button"
                variant={ButtonVariants.tertiary}
                size={ButtonSizes.md}
                disabled={step === STEPS.declaration}
                onClick={handlePrevious}
              >
                {t('COMMON_PREVIOUS')}
              </Button>

              <div className={styles.footerRight}>
                {step === LAST_STEP ? (
                  <Button
                    type="submit"
                    variant={ButtonVariants.primary}
                    size={ButtonSizes.md}
                    disabled={isSaving}
                    loading={isSaving}
                  >
                    {t('COMMON_SAVE')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant={ButtonVariants.primary}
                    size={ButtonSizes.md}
                    onClick={handleNext}
                  >
                    {t('COMMON_NEXT')}
                  </Button>
                )}
              </div>
            </Form.Footer>
          </>
        );
      }}
    </Form>
  );
};
